<script setup>
import { computed, h, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useAccountStatusIndicator } from 'dashboard/composables/useAccountStatusIndicator';

import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'next/dropdown-menu/base';
import Icon from 'next/icon/Icon.vue';
import Button from 'next/button/Button.vue';
import ToggleSwitch from 'dashboard/components-next/switch/Switch.vue';
import { getUserPermissions } from 'dashboard/helper/permissionsHelper';

const { t } = useI18n();
const store = useStore();
const currentUserAvailability = useMapGetter('getCurrentUserAvailability');
const currentAccountId = useMapGetter('getCurrentAccountId');
const currentUserAutoOffline = useMapGetter('getCurrentUserAutoOffline');
const currentUser = useMapGetter('getCurrentUser');
const canManageAutoOffline = computed(() =>
  getUserPermissions(currentUser.value, currentAccountId.value).includes(
    'profile.auto_offline_manage'
  )
);

const { isImpersonating } = useImpersonation();

const statuses = useMapGetter('accountStatuses/getStatuses');
const { indicatorFor } = useAccountStatusIndicator();
const statusColors = {
  online: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
  offline: 'bg-n-slate-9',
};

const availabilityStatuses = computed(() => {
  return statuses.value.map(status => {
    const indicator = indicatorFor(status.key, statusColors) ?? {};
    return {
      label: status.builtin
        ? t(
            `PROFILE_SETTINGS.FORM.AVAILABILITY.STATUS.${status.key.toUpperCase()}`
          )
        : status.description,
      value: status.key,
      indicator,
      icon: h('span', {
        class: ['size-[12px] rounded', indicator.class],
        style: indicator.style,
      }),
      active: currentUserAvailability.value === status.key,
    };
  });
});

onMounted(() => store.dispatch('accountStatuses/get'));

const activeStatus = computed(() => {
  return availabilityStatuses.value.find(status => status.active);
});

const autoOfflineToggle = computed({
  get: () => currentUserAutoOffline.value,
  set: autoOffline => {
    store.dispatch('updateAutoOffline', {
      accountId: currentAccountId.value,
      autoOffline,
    });
  },
});

async function changeAvailabilityStatus(availability) {
  if (isImpersonating.value) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.IMPERSONATING_ERROR'));
    return;
  }
  try {
    await store.dispatch('updateAvailability', {
      availability,
      account_id: currentAccountId.value,
    });
  } catch (error) {
    useAlert(t('PROFILE_SETTINGS.FORM.AVAILABILITY.SET_AVAILABILITY_ERROR'));
  }
}
</script>

<template>
  <DropdownSection class="[&>ul]:overflow-visible">
    <DropdownItem v-if="activeStatus" preserve-open class="gap-1">
      <div class="flex-grow flex items-center gap-1 min-w-0">
        {{ $t('SIDEBAR.SET_YOUR_AVAILABILITY') }}
      </div>
      <DropdownContainer class="shrink-0">
        <template #trigger="{ toggle }">
          <Button
            size="sm"
            color="slate"
            variant="faded"
            icon="i-lucide-chevron-down"
            trailing-icon
            @click="toggle"
          >
            <div class="flex gap-1 items-center min-w-0 text-sm">
              <div class="p-1 flex-shrink-0">
                <div
                  class="size-2 rounded-sm"
                  :class="activeStatus?.indicator?.class"
                  :style="activeStatus?.indicator?.style"
                />
              </div>
              <span class="truncate max-w-[7rem]">
                {{ activeStatus?.label }}
              </span>
            </div>
          </Button>
        </template>
        <DropdownBody class="min-w-32 z-20">
          <DropdownItem
            v-for="status in availabilityStatuses"
            :key="status.value"
            :label="status.label"
            :icon="status.icon"
            class="cursor-pointer"
            @click="changeAvailabilityStatus(status.value)"
          />
        </DropdownBody>
      </DropdownContainer>
    </DropdownItem>
    <DropdownItem v-if="canManageAutoOffline">
      <div class="flex-grow min-w-0">
        {{ $t('SIDEBAR.SET_AUTO_OFFLINE.TEXT') }}
        <Icon
          v-tooltip.top="$t('SIDEBAR.SET_AUTO_OFFLINE.INFO_SHORT')"
          icon="i-lucide-info"
          class="inline-block align-middle ms-1 size-4 text-n-slate-10"
        />
      </div>
      <ToggleSwitch v-model="autoOfflineToggle" />
    </DropdownItem>
  </DropdownSection>
</template>
